/**
 * Apple In-App Purchase via cordova-plugin-purchase (CdvPurchase) v13+.
 *
 * Canonical pattern from shared-docs/in-app-purchases.md:
 *   approved   → optimistic local-state update via onPurchaseUpdate callback,
 *                queue transactionId → productId in pendingVerify,
 *                + transaction.verify() to advance the state machine
 *   verified   → drain pendingVerify into silent backend verifies, then
 *                receipt.finish(); refetch only after the writes settle
 *   userInitiatedAt window guards against cross-user receipt contamination
 *   store.order() return value is checked (it does NOT throw on failure)
 *   the FULL cancel-code set (6777006/6777003/6777010) + a /cancel/i message
 *   match throw the 'canceled' sentinel (no toast) — a sheet dismiss actually
 *   resolves as 6777006 PAYMENT_CANCELLED, not 6777003
 *
 * Build 5 (rejected by Apple, 2.1(b)) used a flat 15s store.ready() timeout that
 * resolved even when products hadn't loaded — leaving `store.get(productId)` null
 * and a "Subscription products are still loading" toast in front of the reviewer.
 * Build 6 polls actively for the specific product instead of relying on store.ready,
 * and pushes an optimistic update on `approved` so the paywall reflects success
 * even before the backend verify lands.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

export const PRODUCT_IDS = [
  'uk.co.wolftechnologies.streaks.premium.monthly',
  'uk.co.wolftechnologies.streaks.premium.yearly',
];

export const PRODUCT_ID_MAP = {
  premium_month: 'uk.co.wolftechnologies.streaks.premium.monthly',
  premium_year: 'uk.co.wolftechnologies.streaks.premium.yearly',
};

const APPLE_TO_INTERNAL = {
  [PRODUCT_ID_MAP.premium_month]: { plan: 'premium', interval: 'monthly' },
  [PRODUCT_ID_MAP.premium_year]: { plan: 'premium', interval: 'yearly' },
};

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';
const USER_INITIATED_WINDOW_MS = 5 * 60 * 1000;
const PRODUCT_POLL_INTERVAL_MS = 500;
const PRODUCT_POLL_TIMEOUT_MS = 30000;

// Canon: shared-docs/in-app-purchases.md — the FULL cancel-code set. A sheet
// dismiss resolves as 6777006 (PAYMENT_CANCELLED); 6777003 (PURCHASE) and
// 6777010 (UNKNOWN / Ask-to-Buy) have both been seen for cancels too. All three
// (plus a /cancel/i message match) must stay silent on a purchase control —
// toasting one is the Guideline 2.1(b) pattern that got BabyTrack build 31
// rejected. Shared by purchaseProduct and restorePurchases so they can't drift.
const CANCEL_CODES = new Set([6777006, 6777003, 6777010]);
// 6777011 REFRESH_RECEIPTS: the adapter refreshes the receipt AFTER the restored
// transactions have already been delivered, so a failure there does not mean the
// restore failed. Silent on restore only — it is not an order outcome.
const RESTORE_SILENT_CODES = new Set([...CANCEL_CODES, 6777011]);

function isCancel(result) {
  return CANCEL_CODES.has(result.code) || /cancel/i.test(result.message || '');
}

let userInitiatedAt = 0;
let restoreInitiatedAt = 0;
let initialized = false;
let onPurchaseUpdate = null;

// Transactions the user just approved (purchase or restore), keyed
// transactionId → productId. The object CdvPurchase hands to `verified` is a
// VerifiedReceipt — it has NO `.transaction` and NO `.productId` (only id,
// sourceReceipt, collection, latestReceipt, nativeTransactions, warning,
// validationDate), so the old handler read `undefined` and the apple-verify
// POST never fired. `approved` is the only place the id is known; `verified`
// drains this map. Fleet fix 2026-09-05 — canon: shared-docs/in-app-purchases.md.
const pendingVerify = new Map();

function getStore() {
  return window.CdvPurchase?.store || null;
}

async function getIdToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

// Silent (toast-free) POST to the backend verify route. Returns { ok, permanent }:
//   ok:true               → backend recorded the subscription.
//   ok:false permanent    → definitive rejection (4xx, e.g. wrong account).
//   ok:false !permanent   → transient (network / 5xx / no token).
// The receipt is finished regardless (canon) — StoreKit 1 only replays
// UNFINISHED transactions, so a failed link is recovered via the user-initiated
// Restore Purchases button, which re-runs approved → verified inside the window.
async function verifyTransaction(transactionId, productId) {
  if (!transactionId) return { ok: false, permanent: true };
  const idToken = await getIdToken();
  if (!idToken) return { ok: false, permanent: false };
  try {
    const res = await fetch(`${API_ENDPOINT}/subscription/apple-verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: idToken },
      body: JSON.stringify({ transactionId, productId }),
    });
    if (res.ok) {
      // A 2xx is NOT automatically a link: apple-verify answers
      // 200 {success:false} for a transaction it refuses (expired, or carrying
      // a revocationDate after a refund / Family Sharing revoke). Those are
      // definitive — they will never become linkable — so report them as such
      // rather than as a successful write.
      const body = await res.json().catch(() => null);
      if (body && body.success === false) {
        console.warn('IAP verify refused:', body.status || 'unknown');
        return { ok: false, permanent: true };
      }
      return { ok: true, permanent: false };
    }
    console.warn('IAP verify failed:', res.status);
    return { ok: false, permanent: res.status >= 400 && res.status < 500 && res.status !== 429 };
  } catch (err) {
    console.warn('IAP verify error:', err.message);
    return { ok: false, permanent: false };
  }
}

export function setOnPurchaseUpdate(cb) {
  onPurchaseUpdate = typeof cb === 'function' ? cb : null;
}

export function initializeIAP() {
  if (initialized) return;
  const store = getStore();
  if (!store) return;
  initialized = true;

  const { ProductType, Platform } = window.CdvPurchase;

  store.register(
    PRODUCT_IDS.map((id) => ({
      id,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    })),
  );

  // CdvPurchase v13: each handler registers via its OWN store.when() call —
  // chaining .approved().verified() throws at init if any link is missing.
  store.when().approved((transaction) => {
    if (!transaction.transactionId) {
      transaction.finish();
      return;
    }
    const productId = transaction.products?.[0]?.id;
    const sub = APPLE_TO_INTERNAL[productId];
    if (Date.now() - userInitiatedAt >= USER_INITIATED_WINDOW_MS) {
      // Stale re-delivered receipt for a previous user/session — discard silently.
      transaction.finish();
      return;
    }
    // INERT on the bridge this app ships. `expirationDate` is only ever set by
    // SKTransaction.refresh when the native layer supplies expirationDateMs
    // (www/store.js:5090), and the classic StoreKit 1 cordova bridge — the one
    // selected here, since neither window.CdvPurchaseCapacitor nor
    // window.CdvPurchaseStoreKit2 exists (store.js:3124-3135) — hard-codes
    // `undefined` for that argument on both purchased (store.js:4753) and
    // restored (store.js:4766); InAppPurchase.m's callbackArgs carries no
    // expiration field at all. So `isExpired` is ALWAYS false here. Kept as the
    // canon best-effort guard for a future SK2/Capacitor bridge only — expiry is
    // enforced by the backend apple-verify, and nothing downstream may treat
    // `approved` as proof of entitlement (see the `restored` flag below).
    const expiresAt = transaction.expirationDate ? new Date(transaction.expirationDate) : null;
    const isExpired = Boolean(expiresAt && expiresAt.getTime() <= Date.now());
    // Unknown product = the plugin's virtual `appstore.application` transaction
    // (always first in the Apple receipt, fires approved like a sale) or an
    // unregistered SKU — never queue it for the backend.
    if (!sub || isExpired) {
      transaction.finish();
      return;
    }
    if (onPurchaseUpdate) {
      try {
        // `restored: true` = this approval came from the Restore Purchases
        // button, where StoreKit re-delivers EVERY historical transaction and
        // (see above) carries no expiry — so it is NOT proof of entitlement and
        // the UI must not claim success off it. Only the refetched backend row
        // can say whether the subscription is actually live.
        onPurchaseUpdate({
          ...sub,
          productId,
          expiresAt: expiresAt ? expiresAt.toISOString() : null,
          restored: restoreInitiatedAt > 0 && Date.now() - restoreInitiatedAt < USER_INITIATED_WINDOW_MS,
        });
      } catch (e) { console.warn(e); }
    }
    pendingVerify.set(transaction.transactionId, productId);
    transaction.verify();
  });

  store.when().verified((receipt) => {
    // `receipt` is a VerifiedReceipt: the local transactions live on
    // receipt.sourceReceipt.transactions. Send exactly what `approved` queued
    // (already window-gated), and only ids this receipt carries.
    const localIds = new Set(
      (receipt?.sourceReceipt?.transactions || []).map((t) => t.transactionId).filter(Boolean),
    );
    const writes = [];
    for (const [transactionId, productId] of pendingVerify) {
      if (localIds.size && !localIds.has(transactionId)) continue; // another receipt
      pendingVerify.delete(transactionId);
      writes.push(verifyTransaction(transactionId, productId));
    }
    receipt.finish();
    if (writes.length && onPurchaseUpdate) {
      // Refetch AFTER the backend row exists — fetching before the write lands
      // races it and the paywall reads back Free.
      Promise.allSettled(writes).then(() => {
        try { onPurchaseUpdate({ refetch: true }); } catch (e) { console.warn(e); }
      });
    }
  });

  // Store.prototype.error(cb) — NOT store.when().error(): the v13 When builder
  // has no `error` method, so the old call threw before store.initialize()
  // and the store never started.
  store.error((err) => {
    console.warn('CdvPurchase store error:', err);
  });

  store.initialize([Platform.APPLE_APPSTORE]);
}

/**
 * Resolve once a specific product is available from the store. CdvPurchase
 * fetches product info from StoreKit asynchronously; on iPad sandbox this can
 * take 10–25 s. Active polling for the product is more reliable than a flat
 * store.ready() timeout (which can resolve even when products aren't loaded).
 */
async function whenProductLoaded(productId) {
  const store = getStore();
  if (!store) return null;
  const deadline = Date.now() + PRODUCT_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const product = store.get(productId);
    if (product && product.canPurchase !== false) {
      const offer = typeof product.getOffer === 'function' ? product.getOffer() : null;
      if (offer) return { product, offer };
    }
    await new Promise((r) => setTimeout(r, PRODUCT_POLL_INTERVAL_MS));
  }
  return null;
}

export async function whenIAPReady() {
  // Kept for backwards-compat with Premium.jsx's "Loading…" guard.
  // Resolves as soon as ANY of our products is loaded (good signal that the
  // App Store handshake completed and Subscribe is safe to enable).
  for (const id of PRODUCT_IDS) {
    const ready = await whenProductLoaded(id);
    if (ready) return;
  }
}

export async function purchaseProduct(productId) {
  const store = getStore();
  if (!store) throw new Error('Purchases are not available on this device.');

  const ready = await whenProductLoaded(productId);
  if (!ready) {
    throw new Error('Subscription is unavailable right now. Please check your connection and try again.');
  }

  userInitiatedAt = Date.now();
  restoreInitiatedAt = 0; // this approval is a real sale, not a restore
  const result = await store.order(ready.offer);
  if (result && result.isError) {
    if (isCancel(result)) {
      const cancelled = new Error('canceled');
      cancelled.cancelled = true;
      throw cancelled;
    }
    throw new Error(result.message || `Purchase failed (code ${result.code || 'unknown'})`);
  }
  return result;
}

export async function restorePurchases() {
  const store = getStore();
  if (!store) throw new Error('Purchases are not available on this device.');
  userInitiatedAt = Date.now();
  restoreInitiatedAt = userInitiatedAt;
  // Same contract as store.order(): a StoreKit restore failure RESOLVES with an
  // IError rather than throwing, so surface it or the UI's failure toast is
  // unreachable.
  const result = await store.restorePurchases();
  // Cancels, and REFRESH_RECEIPTS, must NEVER surface as an error: a red toast
  // on a purchase control is the Guideline 2.1(b) pattern.
  if (result && result.isError
      && !RESTORE_SILENT_CODES.has(result.code)
      && !/cancel/i.test(result.message || '')) {
    const err = new Error(result.message || 'Restore failed');
    err.code = result.code;
    throw err;
  }
  return result;
}
